import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { getApiErrorMessage } from '@/shared/utils/apiError'
import { toIndianDatetimeLocal } from '@/shared/utils/dateTime'
import { Button } from '@/shared/components/ui/Button'
import { Input } from '@/shared/components/ui/Input'
import { Modal } from '@/shared/components/ui/Modal'
import { Select } from '@/shared/components/ui/Select'
import { Textarea } from '@/shared/components/ui/Textarea'
import {
  useCallHistoryWriter,
  useCallLogWriter,
  useDealNoteWriter,
  useFollowupWriter,
  useMeetingWriter,
  uploadLeadRecording,
  type AssignableUser,
} from './api'

export type ActivityKind = 'call' | 'followup' | 'history' | 'meeting' | 'recording' | 'note'

export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  call: 'Log Call',
  followup: 'New Follow-up',
  history: 'Add History',
  meeting: 'Book Appointment',
  recording: 'Upload Recording',
  note: 'Add Note',
}

const opt = (value: string, label: string) => ({ value, label })

function nowLocal() {
  return toIndianDatetimeLocal(new Date())
}

interface Props {
  kind: ActivityKind
  onClose: () => void
  dealId: string
  dealTitle: string
  leadId: string | null
  employees: AssignableUser[]
  currentUserId?: string
}

export function AddActivityDialog({ kind, onClose, dealId, dealTitle, leadId, employees, currentUserId }: Props) {
  const [type, setType] = useState('Outbound')
  const [outcome, setOutcome] = useState('Connected')
  const [duration, setDuration] = useState('10')
  const [rep, setRep] = useState(currentUserId || employees[0]?.id || '')
  const [notes, setNotes] = useState('')
  const [title, setTitle] = useState('')
  const [when, setWhen] = useState(nowLocal())
  const [priority, setPriority] = useState('High')
  const [summary, setSummary] = useState('')
  const [nextAction, setNextAction] = useState('')
  const [meetingType, setMeetingType] = useState('Google Meet')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const callLog = useCallLogWriter(leadId)
  const followup = useFollowupWriter(leadId)
  const history = useCallHistoryWriter(leadId)
  const meeting = useMeetingWriter(leadId)
  const dealNote = useDealNoteWriter(dealId)

  useEffect(() => {
    setType('Outbound')
    setOutcome('Connected')
    setNotes('')
    setTitle('')
    setSummary('')
    setNextAction('')
    setWhen(nowLocal())
    setFile(null)
    setBusy(false)
  }, [kind, dealTitle])

  const repOptions = employees.map((e) => opt(e.id, e.name))
  const needsLead = kind !== 'note'

  // The people picker arrives after the dialog opens, and the API rejects a
  // writer who is not one of them, so keep the choice inside the real options.
  useEffect(() => {
    if (employees.length === 0) return
    if (employees.some((e) => e.id === rep)) return
    setRep(employees.some((e) => e.id === currentUserId) ? currentUserId! : employees[0].id)
  }, [employees, rep, currentUserId])

  const close = () => {
    setNotes('')
    setTitle('')
    setSummary('')
    onClose()
  }

  const submit = async () => {
    if (needsLead && !leadId) {
      toast.error('This deal was not created from a lead, so there is nothing to record against.')
      return
    }
    setBusy(true)
    try {
      if (kind === 'call') {
        if (!notes.trim()) throw new Error('Add a short note about the call.')
        await callLog.mutateAsync({
          type,
          outcome,
          durationMinutes: Number(duration) || 0,
          repUserId: rep,
          notes: notes.trim(),
          occurredAt: when,
        })
      } else if (kind === 'followup') {
        if (!title.trim()) throw new Error('Give the follow-up a title.')
        await followup.mutateAsync({
          title: title.trim(),
          type: 'Call',
          dueAt: when,
          repUserId: rep,
          priority,
          notes: notes.trim() || undefined,
        })
      } else if (kind === 'history') {
        if (!title.trim() || !summary.trim()) throw new Error('A history entry needs a title and a summary.')
        await history.mutateAsync({
          title: title.trim(),
          repUserId: rep,
          occurredAt: when,
          durationMinutes: Number(duration) || 0,
          outcome,
          summary: summary.trim(),
          nextAction: nextAction.trim() || undefined,
        })
      } else if (kind === 'meeting') {
        if (!title.trim()) throw new Error('Give the appointment a title.')
        await meeting.mutateAsync({
          title: title.trim(),
          scheduledAt: when,
          durationMinutes: Math.max(1, Math.round(Number(duration)) || 30),
          type: meetingType,
          hostUserId: rep,
          agenda: notes.trim() || undefined,
        })
      } else if (kind === 'recording') {
        if (!file || !leadId) throw new Error('Choose an audio file to upload.')
        await uploadLeadRecording(leadId, file)
      } else {
        if (!notes.trim()) throw new Error('Write the note first.')
        await dealNote.mutateAsync(notes.trim())
      }
      toast.success(`${ACTIVITY_KIND_LABEL[kind]} added to ${dealTitle}`)
      close()
    } catch (err) {
      toast.error(getApiErrorMessage(err, `save the ${ACTIVITY_KIND_LABEL[kind].toLowerCase()}`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open
      onClose={close}
      title={`${ACTIVITY_KIND_LABEL[kind]} — ${dealTitle}`}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={close}>
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {needsLead && !leadId && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
            This deal has no lead behind it, so calls, follow-ups, histories, meetings and recordings cannot be
            logged. Use Add Note instead.
          </p>
        )}

        {(kind === 'call' || kind === 'history' || kind === 'meeting') && (
          <div className="grid grid-cols-2 gap-3">
            {kind === 'call' && (
              <Select
                label="Direction"
                value={type}
                onValueChange={setType}
                options={[opt('Outbound', 'Outbound'), opt('Inbound', 'Inbound')]}
              />
            )}
            {(kind === 'call' || kind === 'history') && (
              <Select
                label="Outcome"
                value={outcome}
                onValueChange={setOutcome}
                options={[
                  opt('Connected', 'Connected'),
                  opt('Left Voicemail', 'Left Voicemail'),
                  opt('Busy', 'Busy'),
                  opt('No Answer', 'No Answer'),
                  opt('Wrong Number', 'Wrong Number'),
                  opt('Scheduled Demo', 'Scheduled Demo'),
                ]}
              />
            )}
            {kind === 'meeting' && (
              <Select
                label="Where"
                value={meetingType}
                onValueChange={setMeetingType}
                options={[
                  opt('Google Meet', 'Google Meet'),
                  opt('Zoom', 'Zoom'),
                  opt('Microsoft Teams', 'Microsoft Teams'),
                  opt('Phone Call', 'Phone Call'),
                  opt('In-Person', 'In-Person'),
                ]}
              />
            )}
            <Input
              label={kind === 'meeting' ? 'Duration (minutes)' : 'Duration (minutes)'}
              type="number"
              min="0"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
        )}

        {kind !== 'recording' && kind !== 'note' && (
          <div className="grid grid-cols-2 gap-3">
            <Input
              label={kind === 'followup' ? 'Due' : kind === 'meeting' ? 'Scheduled for' : 'When'}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            {kind === 'followup' ? (
              <Select
                label="Priority"
                value={priority}
                onValueChange={setPriority}
                options={[opt('High', 'High'), opt('Normal', 'Normal'), opt('Low', 'Low')]}
              />
            ) : (
              <Select label="By" value={rep} onValueChange={setRep} options={repOptions} />
            )}
          </div>
        )}

        {kind === 'followup' && (
          <Select label="By" value={rep} onValueChange={setRep} options={repOptions} />
        )}

        {(kind === 'followup' || kind === 'history' || kind === 'meeting') && (
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={kind === 'history' ? 'Discovery call' : kind === 'meeting' ? 'Solution walkthrough' : 'Call back on pricing'}
          />
        )}

        {kind === 'history' && (
          <>
            <Textarea label="Summary" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
            <Input
              label="Next action"
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              placeholder="Send the revised quotation"
            />
          </>
        )}

        {(kind === 'call' || kind === 'followup' || kind === 'meeting' || kind === 'note') && (
          <Textarea
            label={kind === 'note' ? 'What happened?' : 'Notes'}
            rows={kind === 'note' ? 4 : 2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={kind === 'note' ? 'Client is interested in digital marketing services.' : ''}
          />
        )}

        {kind === 'recording' && (
          <div className="space-y-2">
            <input
              ref={inputRef}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
              {file ? file.name : 'Choose an audio file'}
            </Button>
            <p className="text-xs text-muted-foreground">Stored with the lead, playable by anyone who can open it.</p>
          </div>
        )}
      </div>
    </Modal>
  )
}
